import _ from 'lodash'
import { withCompPreviewInfo } from '@wix/editor-elements-integrations'
import { getTextPresets, ooiStyleParamsUtil, processOoiCss } from '@wix/thunderbolt-commons'
import { isOOI } from './IsOOI'
import { getColorValue, getSiteColors, getTpaFontParamValue, splitPropertiesValues } from './tpaStyleUtils'
import { getHeightInPixels, getWidthInPixels } from '@wix/editor-elements-common-utils'
import type { ColorMap, CompDefinition, StyleProperties, ThemeFontMap } from '@wix/thunderbolt-becky-types'
import type { WidgetDataViewItem } from './TPAWidget'
import type { Experiments } from '@wix/thunderbolt-symbols'

type TPAWidgetDefinition = CompDefinition<never, WidgetDataViewItem, never, never>

const { shouldOverrideStyleParams, overrideStyleParams } = ooiStyleParamsUtil
const isPublicSegment = process.env.PACKAGE_NAME !== 'thunderbolt-ds'

const isCssVarsOnlyWidget = (isCssPerBreakpoint: boolean, experiments: Experiments) =>
	isPublicSegment && isCssPerBreakpoint && !!experiments['specs.thunderbolt.ooiCssAsLinkTag']

export const resolveStyles = (
	siteFonts: ThemeFontMap,
	themeColors: ColorMap,
	rawStyleProperties: StyleProperties,
	getFontsFromExternal: boolean,
	experiments: Experiments
) => {
	const _siteFonts = _.values(siteFonts)
	const {
		color: colorParams,
		number: numberParams,
		boolean: booleanParams,
		font: fontParams,
	} = splitPropertiesValues(rawStyleProperties)

	return {
		siteColors: getSiteColors(themeColors, experiments),
		siteTextPresets: getTextPresets(_siteFonts, getFontsFromExternal),
		style: {
			colors: _.mapValues(colorParams, (color, name) =>
				getColorValue(color, rawStyleProperties[`alpha-param_color_${name}`], themeColors)
			),
			numbers: _.mapValues(numberParams, Number),
			booleans: _.mapValues(booleanParams, (value) => value && value !== 'false'),
			fonts: _(fontParams)
				.mapValues((fontParam) => getTpaFontParamValue(fontParam, _siteFonts, getFontsFromExternal))
				.pickBy((v) => !!v)
				.value(),
			googleFontsCssUrl: '',
			uploadFontFaces: '',
		},
	}
}

export const css = withCompPreviewInfo<object, TPAWidgetDefinition>()(
	[
		'compId',
		'compData',
		'styleProperties',
		// @ts-ignore
		'formFactor',
		'language',
		'siteColors',
		'siteFonts',
		'compLayout',
		'hasResponsiveLayout',
		'experiments',
		// @ts-ignore
		'anywhereThemeOverride',
		'compSingleLayout',
		'isOneDocMigrated',
		...(process.env.PACKAGE_NAME === 'thunderbolt-ds'
			? (['componentViewMode', 'isLivePreviewOpenForEditor', 'isAdi'] as const)
			: []),
	],
	({
		compId,
		compData,
		styleProperties,
		// @ts-ignore
		formFactor,
		language,
		siteColors: themeColors,
		siteFonts,
		compLayout,
		hasResponsiveLayout,
		experiments,
		// @ts-ignore
		anywhereThemeOverride,
		componentViewMode,
		// @ts-ignore
		isLivePreviewOpenForEditor,
		// @ts-ignore
		isAdi,
		compSingleLayout,
		isOneDocMigrated,
		// @ts-ignore
		runAndReport,
	}) => {
		const shouldRenderAsOoi = isOOI({
			widgetSpec: compData.widgetSpecData,
			isMigratingToOoi: compData.isMigratingToOoi,
			componentViewMode,
			isLivePreviewOpenForEditor,
			isAdi,
		})

		const disableNoCssBundle =
			process.env.PACKAGE_NAME === 'thunderbolt-ds' &&
			!!compData.referenceId &&
			['621bc837-5943-4c76-a7ce-a0e38185301f', 'cc882051-73c9-41a6-8f90-f6ebc9f10fe1'].includes(compData.widgetId)

		if (!shouldRenderAsOoi || !compData.cssConfig || disableNoCssBundle) {
			return {}
		}

		const getFontsFromExternal = !!experiments['specs.thunderbolt.fontsFromExternal']

		const styleData = resolveStyles(siteFonts, themeColors, styleProperties, getFontsFromExternal, experiments)

		const { widgetId, appSettings, tpaData } = compData
		if (shouldOverrideStyleParams(widgetId)) {
			styleData.style = overrideStyleParams({
				styleParams: styleData.style,
				widgetId,
				appSettings,
				tpaData,
				isResponsive: hasResponsiveLayout,
				anywhereThemeOverride,
			})
		}

		// TODO: use isClassicComponent api when this PR merged: https://github.com/wix-private/thunderbolt/pull/33237
		const isClassicComponent = !hasResponsiveLayout
		const getClassicComponentWidth = (_isOneDocMigrated: boolean) => {
			if (_isOneDocMigrated) {
				return getWidthInPixels(compSingleLayout) || ('' as const)
			}
			return compLayout.docked?.left ? ('' as const) : (compLayout.width as number)
		}

		const getClassicComponentHeight = (_isOneDocMigrated: boolean) => {
			if (_isOneDocMigrated) {
				return getHeightInPixels(compSingleLayout) || ('' as const)
			}
			return compLayout.height as number
		}

		const dimensions = isClassicComponent
			? { width: getClassicComponentWidth(isOneDocMigrated), height: getClassicComponentHeight(isOneDocMigrated) }
			: { width: '' as const, height: '' as const }

		const cssString = processOoiCss({
			styleData,
			prefixSelector: `.${compId}`,
			withStaticCss: true,
			isRTL: language === 'he' || language === 'ar',
			isMobile: formFactor === 'mobile',
			dimensions,
			cssConfig: compData.cssConfig,
			usesCssPerBreakpoint: !!compData.usesCssPerBreakpoint,
			onlyCssVars: isCssVarsOnlyWidget(!!compData.isCssPerBreakpoint, experiments),
			runAndReport: runAndReport || ((fn) => fn()),
		})

		return cssString
	}
)
